import { useEffect, useState } from "react";
import { Truck, Save } from "lucide-react";
import { Button } from "../../components/UI/Button";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useParams } from "react-router-dom";
import { companyPricingUseCase } from "di/container";
import type {
  ShippingFeeSetting,
  ShippingFeeSettingRequest,
} from "domain/types/pricing";
import { Input } from "../../components/UI/Input";
import ToggleableButton from "../../components/UI/ToggleagleButton";

const ShippingFeeManagementPage: React.FC = () => {
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [shippingFee, setShippingFee] = useState<ShippingFeeSetting | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ShippingFeeSettingRequest>({
    baseFee: 3000,
    freeShippingThreshold: 50000,
  });
  const [hasChanges, setHasChanges] = useState(false);

  const fetchShippingFee = async () => {
    if (!Number.isFinite(cid)) return;
    try {
      setIsLoading(true);
      const result = await companyPricingUseCase.getShippingFee(cid);
      setShippingFee(result);
      if (result) {
        setFormData({
          baseFee: result.baseFee,
          freeShippingThreshold: result.freeShippingThreshold,
        });
      }
    } catch (e) {
      console.error(e);
      // 배송비 설정이 없는 경우는 정상적인 상황이므로 에러 표시하지 않음
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShippingFee();
  }, [companyId]);

  useEffect(() => {
    if (shippingFee) {
      const changed =
        formData.baseFee !== shippingFee.baseFee ||
        formData.freeShippingThreshold !== shippingFee.freeShippingThreshold;
      setHasChanges(changed);
    } else {
      setHasChanges(true);
    }
  }, [formData, shippingFee]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const result = await companyPricingUseCase.updateShippingFee(
        cid,
        formData,
      );
      setShippingFee(result);
      setHasChanges(false);
      alert("배송비 설정이 저장되었습니다.");
    } catch (e) {
      console.error(e);
      alert("배송비 설정 저장에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("ko-KR").format(value);
  };

  return (
    <CompanyAdminLayout>
      <div className="p-5">
        <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
          <h2 className="text-[1.375rem] font-semibold">배송비 관리</h2>
          <p className="text-[1rem] font-normal">
            기본 배송비와 무료배송 기준 금액을 설정합니다.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-10 text-gray-500">로딩 중...</div>
        ) : (
          <div className="max-w-[700px]">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="w-full flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-start">
                  <h3 className="font-semibold text-gray-900">배송비 설정</h3>
                  <p className="text-sm text-gray-500">
                    주문 시 적용되는 배송비를 설정합니다.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* 기본 배송비 */}
                <div>
                  <label className="text-start block text-sm font-medium text-gray-700 mb-2">
                    기본 배송비
                  </label>
                  <div className="flex justify-between items-center relative">
                    <div className="w-full relative">
                      <Input
                        type="number"
                        value={formData.baseFee}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            baseFee: Number(e.target.value),
                          })
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-lg focus:border-[#4a6cf7] focus:outline-none focus:ring-1 focus:ring-[#4a6cf7]"
                        placeholder="3000"
                      />
                    </div>
                    <span className="ml-3 text-nowrap text-gray-500">원</span>
                  </div>

                  <p className="mt-2 text-xs text-start text-gray-500">
                    무료배송 조건을 충족하지 않은 주문에 부과되는 배송비입니다.
                  </p>
                </div>

                {/* 무료배송 기준 금액 */}
                <div>
                  <label className="text-start block text-sm font-medium text-gray-700 mb-2">
                    무료배송 기준 금액
                  </label>
                  <div className="flex justify-between items-center relative">
                    <div className="relative w-full">
                      <Input
                        type="number"
                        value={formData.freeShippingThreshold ?? ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            freeShippingThreshold: e.target.value
                              ? Number(e.target.value)
                              : null,
                          })
                        }
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-lg focus:border-[#4a6cf7] focus:outline-none focus:ring-1 focus:ring-[#4a6cf7]"
                        placeholder="무료배송 없음"
                      />
                    </div>
                    <span className="ml-3 text-nowrap text-gray-500">
                      원 이상
                    </span>
                  </div>

                  <p className="text-start mt-2 text-xs text-gray-500">
                    이 금액 이상 주문 시 배송비가 무료입니다. 비워두면
                    무료배송이 적용되지 않습니다.
                  </p>
                </div>

                {/* 미리보기 */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-5">
                    적용 미리보기
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">기본 배송비:</span>
                      <span className="font-medium">
                        {formatCurrency(formData.baseFee)}원
                      </span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-gray-600">무료배송 조건:</span>
                      <span className="font-medium">
                        {formData.freeShippingThreshold
                          ? `${formatCurrency(formData.freeShippingThreshold)}원 이상 주문`
                          : "무료배송 없음"}
                      </span>
                    </div>
                    <hr className="h-[10px]" />
                    <div className="text-xs text-gray-500">
                      {formData.freeShippingThreshold ? (
                        <>
                          <p>
                            • {formatCurrency(formData.freeShippingThreshold)}원
                            미만 주문: {formatCurrency(formData.baseFee)}원
                            배송비 부과
                          </p>
                          <p>
                            • {formatCurrency(formData.freeShippingThreshold)}원
                            이상 주문: 무료배송
                          </p>
                        </>
                      ) : (
                        <p>
                          • 모든 주문에 {formatCurrency(formData.baseFee)}원
                          배송비가 부과됩니다.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 저장 버튼 */}
              <div className="mt-6 flex justify-end">
                <ToggleableButton
                  onClick={handleSave}
                  isEnabled={!(!hasChanges || isSaving)}
                  className={`h-[2.75rem] px-4 flex items-center gap-2`}
                >
                  <Save size={18} />
                  {isSaving ? "저장 중..." : "저장하기"}
                </ToggleableButton>
              </div>
            </div>

            {/* 도움말 */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="text-sm font-medium text-blue-800 mb-2">
                배송비 설정 안내
              </h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• 배송비는 결제 단계에서 자동으로 계산되어 표시됩니다.</li>
                <li>
                  • 무료배송 기준 금액은 상품 금액 기준으로 적용됩니다 (옵션
                  금액 포함).
                </li>
                <li>• 도서산간 지역 추가 배송비는 별도로 관리되지 않습니다.</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default ShippingFeeManagementPage;
